import { neutral, petrol, textColors } from '../colors';

/**
 * This function defines the typography styles used in the theme.
 * Font Weight Numeric to String Mapping:
 * 400: 'normal', 500: 'medium', 600: 'semibold', 700: 'bold'
 *
 * It includes font family, weight, size, and line height for different text elements
 * like body text, captions, buttons, subtitles, headers, etc.
 *
 * The function returns an object with typography configurations.
 */

const commonFontSettings = {
  fontWeight: 400, // normal
  color: textColors.primaryText,
  lineHeight: 1.6,
};

export const createTypography = () => ({
  fontFamily: ['sofiapro', 'Arial', 'Helvetica', 'Verdana', 'sans-serif'].join(','),
  // Used for the most important and highest-level headings, typically page titles.
  h1: {
    fontSize: '56px',
    fontWeight: 700, // bold
    color: petrol.main,
  },
  // Used for section titles that are subordinate to h1 headings.
  h2: {
    fontSize: '48px',
    fontWeight: 700, // bold
    color: petrol.main,
  },
  // Used for subsection titles that fall under h2 sections.
  h3: {
    fontSize: '40px',
    fontWeight: 600, // semibold
    color: petrol.main,
  },
  // Used for smaller subsections or individual content groupings within a h3 subsection.
  h4: {
    fontSize: '32px',
    fontWeight: 600, // semibold
    color: petrol.main,
  },
  // Used for headings of sidebar sections or other secondary content.
  h5: {
    fontSize: '24px',
    fontWeight: 700, // bold
    color: petrol.main,
  },
  // Used for the smallest section headings or for labeling components like cards.
  h6: {
    fontSize: '20px',
    fontWeight: 600, // semibold
    color: textColors.primaryText,
    textDecoration: 'none',
    textTransform: 'none', // makes sure text is not transformed to uppercase
    letterSpacing: 'normal', // resets letter spacing to default
  },

  // Used for secondary text that elaborates on a title or heading.
  subtitle1: {
    ...commonFontSettings,
    fontSize: '16px',
    color: petrol.alpha75,
  },

  // Used for secondary text that elaborates on a title or heading.
  subtitle2: {
    ...commonFontSettings,
    fontSize: '14px',
    color: petrol.alpha75,
    fontWeight: 500, // medium
  },

  // Used for primary body text in paragraphs.
  body1: {
    ...commonFontSettings,
    fontSize: '16px',
  },

  // Used for secondary body text, often appearing as less important information.
  body2: {
    ...commonFontSettings,
    fontSize: '14px',
    color: '#4F4F4F',
  },

  button: {
    fontWeight: 400,
    color: textColors.primaryText,
  },

  caption: {
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: 1.6,
  },
  overline: {
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: 2.66,
  },

  // Custom variant, explanation needed.
  small: {
    ...commonFontSettings,
    fontSize: '13px',
    lineHeight: 1.15,
    color: neutral['400'],
  },
  // Custom variant, explanation needed.
  tiny: {
    ...commonFontSettings,
    fontSize: '12px',
    lineHeight: 1,
    color: neutral['400'],
  },

  // Line item label - maps to --marketplaceSmallFontStyles (14px/24px, medium weight)
  // Used for price labels and values in BookingBreakdown
  lineItemLabel: {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
    color: textColors.primaryText,
  },

  // Line item muted text - maps to --marketplaceTinyFontStyles (13px/18px, regular weight)
  // Used for "per night" breakdown and fee info text in BookingBreakdown
  lineItemMuted: {
    fontSize: '13px',
    fontWeight: 400,
    lineHeight: 1.38,
    color: neutral[400],
  },

  // Line item day label - maps to --marketplaceH5FontStyles (14px/18px, medium weight)
  // Used for "Booking Start/End" labels in BookingBreakdown
  lineItemDayLabel: {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
    color: neutral[400],
  },

  // Line item highlight - maps to --marketplaceButtonFontStyles (sofiapro, semibold, 16px/24px)
  // Used for weekday display and total price in BookingBreakdown
  lineItemHighlight: {
    fontFamily: '"sofiapro", Helvetica, Arial, sans-serif',
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px',
    letterSpacing: '0.2px',
    color: textColors.primaryText,
  },
});
